import { useState } from 'react';
import { Link } from 'react-router';
import { LayoutGrid, Sun, UserPlus, X } from 'lucide-react';
import type { Me, Person, Project, Task } from '../api.ts';
import { useDashboard, useSave, useTaskDrawer } from '../data.ts';
import { ago, basisLabel, firstName, fmtDate, isOverdue, priorityRank, recentlyByAgent, today } from '../util.ts';
import { BriefingBanner } from '../components/BriefingBanner.tsx';
import { BaselineButton } from './Other.tsx';
import { TeamPulse, WorkspaceHub } from '../components/Hub.tsx';
import { SconceCard } from '../components/SconceCard.tsx';
import { InboxWatch } from '../components/InboxWatch.tsx';
import { Avatar, CommentCount, DateText, Empty, Err, OpsMark, PriorityFlag } from '../components/ui.tsx';

// My Day is your own work plus what nobody has taken; Overview is the whole team. The choice sticks per browser.
type View = 'day' | 'overview';
const VIEW_KEY = 'gbops-cc-view';
const savedView = (): View => { try { return localStorage.getItem(VIEW_KEY) === 'overview' ? 'overview' : 'day'; } catch { return 'day'; } };
type Kpi = { id: string; l: string; tone?: string; pick: (t: Task) => boolean; anyone?: boolean };

const open_ = (t: Task) => t.status !== 'done' && t.status !== 'cancelled';
const byUrgency = (a: Task, b: Task) => priorityRank(a.priority) - priorityRank(b.priority) || (a.end_date ?? '9999').localeCompare(b.end_date ?? '9999');

export function CommandCenter({ me }: { me: Me }) {
  const d = useDashboard();
  const { open } = useTaskDrawer();
  const [view, setViewState] = useState<View>(savedView);
  const [filter, setFilter] = useState<string | null>(null);
  const setView = (v: View) => { setViewState(v); setFilter(null); try { localStorage.setItem(VIEW_KEY, v); } catch { /* private window */ } };
  if (d.error) return <div className="page"><Err e={d.error} /></div>;
  if (!d.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const { tasks, projects, people, briefings, notes, resources } = d.data;
  const live = tasks.filter((t) => t.status !== 'cancelled');
  const day = view === 'day';
  // My Day counts your own tasks; Unassigned is everyone's, since anyone can take it.
  const scope = day ? live.filter((t) => t.owner === me.email) : live;
  const week = new Date(Date.now() + 7 * 86400_000).toISOString().slice(0, 10);
  const kpis: Kpi[] = [
    { id: 'active', l: 'In progress', pick: (t) => t.status === 'active' },
    { id: 'blocked', l: 'Blocked', tone: 'bad', pick: (t) => t.status === 'blocked' },
    { id: 'overdue', l: 'Overdue', tone: 'bad', pick: isOverdue },
    { id: 'week', l: 'Due in 7 days', pick: (t) => open_(t) && !!t.end_date && t.end_date >= today() && t.end_date <= week },
    { id: 'unassigned', l: 'Unassigned', tone: 'warn', anyone: true, pick: (t) => open_(t) && !t.owner },
    ...(day ? [] : [{ id: 'agent', l: 'Moved by Ops (7d)', tone: 'agent', pick: (t: Task) => recentlyByAgent(t) }]),
  ];
  const listFor = (k: Kpi) => (k.anyone ? live : scope).filter(k.pick).sort(byUrgency);
  const active = kpis.find((k) => k.id === filter);
  const unassigned = live.filter((t) => open_(t) && !t.owner).sort(byUrgency);
  const date = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());

  return (
    <div className="page cc">
      <BriefingBanner briefings={briefings} people={people} />
      <header className="cc-head">
        <div><p className="eyebrow">{date}</p><h1>Good {partOfDay()}, {(me.name || me.email).split(' ')[0]}</h1></div>
        <div className="seg cc-view" role="group" aria-label="View">
          <button className={day ? 'on' : ''} aria-pressed={day} onClick={() => setView('day')}><Sun size={13} aria-hidden /> My Day</button>
          <button className={!day ? 'on' : ''} aria-pressed={!day} onClick={() => setView('overview')}><LayoutGrid size={13} aria-hidden /> Overview</button>
        </div>
      </header>
      <div className="kpis">{kpis.map((k) => {
        const n = listFor(k).length;
        return <button key={k.id} className={`kpi ${n ? k.tone ?? '' : ''} ${filter === k.id ? 'on' : ''}`} aria-pressed={filter === k.id}
          onClick={() => setFilter(filter === k.id ? null : k.id)}><b>{n}</b><span>{k.l}{day && !k.anyone ? ' · yours' : ''}</span></button>;
      })}</div>

      <div className="cc-grid">
        <main className="cc-main">
          {active && (
            <section className="card">
              <div className="card-h"><h2>{active.l}{day && !active.anyone ? ' · yours' : ''}</h2><button className="icon" onClick={() => setFilter(null)} aria-label="Clear filter"><X size={16} /></button></div>
              <TaskList tasks={listFor(active)} me={me} people={people} projects={projects} onOpen={open} showOwner={!day || !!active.anyone} empty="Nothing here." />
            </section>
          )}
          {day ? <>
            <InProgress tasks={scope} me={me} people={people} projects={projects} onOpen={open} />
            <Unassigned tasks={unassigned} me={me} people={people} projects={projects} onOpen={open} />
            <section className="card">
              <div className="card-h"><h2>Your projects</h2><Link className="sm" to="/projects">All projects →</Link></div>
              {(() => {
                const yours = projects.filter((p) => p.owner === me.email || scope.some((t) => t.project === p.id && open_(t)));
                return yours.length ? <div className="projects">{yours.map((p) => <ProjectRow key={p.id} p={p} tasks={live.filter((t) => t.project === p.id)} people={people} onOpen={open} />)}</div>
                  : <Empty>You don't lead or have open work in any project.</Empty>;
              })()}
            </section>
          </> : <>
            <InProgress tasks={live} team me={me} people={people} projects={projects} onOpen={open} />
            <Unassigned tasks={unassigned} me={me} people={people} projects={projects} onOpen={open} />
            <section className="card">
              <div className="card-h"><h2>Projects</h2><Link className="sm" to="/timeline">Full timeline →</Link></div>
              <div className="projects">{projects.map((p) => <ProjectRow key={p.id} p={p} tasks={live.filter((t) => t.project === p.id)} people={people} onOpen={open} />)}</div>
            </section>
            <NextMilestones tasks={live} me={me} people={people} projects={projects} onOpen={open} />
          </>}
        </main>
        <aside className="cc-side">
          <InboxWatch />
          <SconceCard />
          {!day && <TeamPulse />}
          <WorkspaceHub resources={resources} />
          <section className="card">
            <div className="card-h"><h2><OpsMark size={14} aria-hidden /> What Ops did lately</h2><Link className="sm" to="/notes">Notes log →</Link></div>
            {notes.length ? <ul className="nlist">{notes.slice(0, 5).map((n) => (
              <li key={n.id}><Avatar email={n.author} people={people} size={20} />
                <div><p className="clamp">{n.summary || n.text}</p><span className="muted sm">{firstName(n.author, people)} · {ago(n.created_at)}{n.status === 'error' ? ' · failed' : n.reverted ? ' · undone' : ''}</span></div></li>
            ))}</ul> : <Empty>Press ⌘K and tell Ops what happened.</Empty>}
          </section>
        </aside>
      </div>
    </div>
  );
}

type ListProps = { me: Me; people: Person[]; projects: Project[]; onOpen: (id: string) => void };

// Work that has started: in progress, plus blocked (started and stuck). Planned work stays on project pages until someone moves it here.
function InProgress({ tasks, team, ...p }: ListProps & { tasks: Task[]; team?: boolean }) {
  const doing = tasks.filter((t) => (t.status === 'active' || t.status === 'blocked') && (!team || !!t.owner)).sort(byUrgency);
  return (
    <section className="card">
      <div className="card-h"><h2>In progress{team ? '' : ' · yours'}</h2><Link className="sm" to={team ? '/projects' : '/my-work'}>{team ? 'All projects →' : 'My work →'}</Link></div>
      <TaskList tasks={doing} {...p} showOwner={team} empty={team ? 'Nobody has work in progress.' : 'Nothing in progress. Take something from Unassigned, or start a planned task from a project.'} />
    </section>
  );
}

// Work nobody owns: take it yourself or hand it to someone.
function Unassigned({ tasks, ...p }: ListProps & { tasks: Task[] }) {
  const [all, setAll] = useState(false);
  const n = 8;
  return (
    <section className="card">
      <div className="card-h"><h2><UserPlus size={14} aria-hidden /> Unassigned</h2><span className="muted sm">{tasks.length || ''}</span></div>
      {tasks.length ? <TaskList tasks={tasks.slice(0, all ? 999 : n)} {...p} assign />
        : <Empty>Everything has an owner.</Empty>}
      {tasks.length > n && <button className="link sm" onClick={() => setAll(!all)}>{all ? 'Show fewer' : `Show all ${tasks.length}`}</button>}
    </section>
  );
}

function TaskList({ tasks, me, people, projects, onOpen, showOwner, assign, empty, className = '' }: ListProps & { tasks: Task[]; showOwner?: boolean; assign?: boolean; empty?: string; className?: string }) {
  const save = useSave();
  const patch = (t: Task, body: Record<string, unknown>) => save.mutate({ path: `/tasks/${t.id}`, method: 'PATCH', body: { ...body, expected_version: t.version } });
  const others = people.filter((x) => x.active && x.email !== me.email);
  if (!tasks.length) return <Empty>{empty ?? '—'}</Empty>;
  return (
    <ul className={`tlist cc-tasks ${className}`}>{tasks.map((t) => (
      <li key={t.id}>
        <PriorityFlag p={t.priority} onChange={(priority) => patch(t, { priority })} />
        <div className="cc-t">
          <button className="link" onClick={() => onOpen(t.id)}>{t.title}</button>
          <span className="muted sm">{projects.find((x) => x.id === t.project)?.key}{t.status === 'blocked' && t.blocker ? ` · Blocked: ${t.blocker}` : ''} <CommentCount n={t.comment_count} /></span>
        </div>
        <DateText t={t} />
        {assign && !t.owner ? (
          <span className="row cc-assign">
            <button className="btn sm primary" onClick={() => patch(t, { owner: me.email })}>Take it</button>
            {others.length > 0 && <select className="sm" value="" aria-label={`Assign ${t.title}`} onChange={(e) => e.target.value && patch(t, { owner: e.target.value })}>
              <option value="">Assign…</option>{others.map((o) => <option key={o.email} value={o.email}>{o.name || o.email}</option>)}</select>}
          </span>
        ) : showOwner && <Avatar email={t.owner} people={people} size={20} />}
      </li>
    ))}</ul>
  );
}

// The next few open milestones across every project, soonest first; the Gantt itself lives on the Timeline page.
function NextMilestones({ tasks, ...p }: ListProps & { tasks: Task[] }) {
  const open_ = tasks.filter((t) => t.kind === 'milestone' && t.status !== 'done');
  const dated = open_.filter((t) => t.end_date).sort((a, b) => a.end_date!.localeCompare(b.end_date!));
  const undated = tasks.filter((t) => !t.start_date && !t.end_date && t.status !== 'done').length;
  return (
    <section className="card">
      <div className="card-h"><h2>Next milestones</h2><Link className="sm" to="/timeline">Full timeline →</Link></div>
      {dated.length ? <TaskList tasks={dated.slice(0, 6)} {...p} showOwner className="ms-list" />
        : <Empty>{open_.length ? 'No milestone has a date yet.' : 'No open milestones.'}</Empty>}
      {undated > 0 && <div className="row sm muted ms-undated">{undated} undated {undated === 1 ? 'item' : 'items'}<BaselineButton undated={undated} inline /></div>}
    </section>
  );
}

// Milestone progress for a project: what's done, the next milestone, its date and how firm it is.
function ProjectRow({ p, tasks, people, onOpen }: { p: Project; tasks: Task[]; people: any[]; onOpen: (id: string) => void }) {
  const ms = tasks.filter((t) => t.kind === 'milestone').sort((a, b) => (a.end_date ?? '9999').localeCompare(b.end_date ?? '9999'));
  const done = tasks.filter((t) => t.status === 'done').length;
  const next = ms.find((t) => t.status !== 'done');
  const blocked = tasks.filter((t) => t.status === 'blocked').length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  return (
    <div className="prow" style={{ '--c': p.color } as React.CSSProperties}>
      <div className="prow-title"><span className="key">{p.key}</span><Link to={`/projects/${p.id}`} className="prow-link"><b>{p.title}</b></Link>{p.owner && <Avatar email={p.owner} people={people} size={18} />}</div>
      <div className="prow-bar" title={`${done} of ${tasks.length} tasks done`}><i style={{ width: `${pct}%` }} /></div>
      <div className="prow-meta">
        <span>{done}/{tasks.length} done</span>
        {blocked > 0 && <span className="bad">{blocked} blocked</span>}
        {next ? <span className="row"><button className="link" onClick={() => onOpen(next.id)}>Next: {next.title}</button>{next.end_date ? <span>{fmtDate(next.end_date)} ({basisLabel[next.date_basis].toLowerCase()})</span> : <span className="tbd">TBD · Unscheduled</span>}</span>
          : <span className="muted">No open milestones</span>}
      </div>
    </div>
  );
}

function partOfDay() { const h = new Date().getHours(); return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening'; }
